import {
  createEntityAdapter,
  createSelector,
  EntityState,
} from '@reduxjs/toolkit';
import { apiSlice } from './api';
import { RootState } from './store';
import { ResponseBody } from '../types/common';
import { SharedLink } from '../types/modal';
import { SharedLibraryParams, SharedLibrary } from '../types/share';

const sharedLibraryAdapter = createEntityAdapter({
  selectId: (data: SharedLibrary) => data.project.projectId,
});
const initialState = sharedLibraryAdapter.getInitialState();

export const shareApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSharedLibrary: builder.query<
      EntityState<SharedLibrary>,
      SharedLibraryParams
    >({
      query: ({
        page = 0,
        search = '',
        size = 24,
        sortBy = 'share_date',
        sortDir = 'DESC',
      }) => ({
        url: 'library/share/detail/projects',
        params: { page, search, size, sortBy, sortDir },
      }),
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.page && arg.page > 0) {
          currentCache.ids.push(...newItems.ids);
          currentCache.ids = [...new Set(currentCache.ids)];
          currentCache.entities = {
            ...currentCache.entities,
            ...newItems.entities,
          };
        } else {
          return newItems;
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
      },
      transformResponse: (res: ResponseBody) =>
        sharedLibraryAdapter.setAll(initialState, res.data),
      providesTags: (result, error, arg) =>
        result
          ? [
              'Share',
              ...result.ids.map((id) => ({
                type: 'Share' as const,
                id,
              })),
            ]
          : ['Share'],
    }),
    getSharedProject: builder.query({
      query: (projectId) => `library/share/detail/projects/${projectId}`,
      providesTags: ['Share'],
    }),
    getSharedLinks: builder.query<SharedLink[], void>({
      query: () => 'library/share/links',
      forceRefetch({ currentArg, previousArg }) {
        return true;
      },
      transformResponse: (res: ResponseBody) => res.data,
    }),
    addSharedLink: builder.mutation({
      query: (data) => ({
        url: 'library/share/links',
        method: 'POST',
        body: data,
      }),
    }),
    addSharedLinkNoti: builder.mutation({
      query: (projectId) => ({
        url: `library/share/links/${projectId}`,
        method: 'PUT',
      }),
      transformResponse: (res: ResponseBody) => res.data,
    }),
    getSharedHistory: builder.query({
      query: (projectId) =>
        `library/share/detail/projects/${projectId}/history`,
      providesTags: ['Share'],
    }),
    delSharedHistory: builder.mutation({
      query: ({ projectId }) => ({
        url: `share/detail/projects/${projectId}/history`,
        method: 'DELETE',
      }),
    }),
    delSharedProject: builder.mutation({
      query: (data) => ({
        url: 'library/share/edit/projects/delete',
        method: 'POST',
        body: { data },
      }),
      invalidatesTags: ['Main', 'Trash'],
    }),
    updateSharedNote: builder.mutation({
      query: ({ projectId, data }) => ({
        url: `library/share/detail/projects/${projectId}/note`,
        method: 'PUT',
        body: { data },
      }),
      invalidatesTags: (result, error, { projectId }) => [
        { type: 'Share', id: projectId },
      ],
    }),
  }),
});

export const {
  useGetSharedLibraryQuery,
  useGetSharedProjectQuery,
  useGetSharedLinksQuery,
  useAddSharedLinkMutation,
  useAddSharedLinkNotiMutation,
  useGetSharedHistoryQuery,
  useDelSharedHistoryMutation,
  useDelSharedProjectMutation,
  useUpdateSharedNoteMutation,
} = shareApiSlice;

export const selectSharedResult =
  shareApiSlice.endpoints.getSharedLibrary.select({
    page: 0,
  });

const selectSharedData = createSelector(
  selectSharedResult,
  (sharedResult) => sharedResult.data
);

export const {
  selectAll: selectAllShared,
  selectById: selectSharedById,
  selectIds: selectSharedIds,
  selectTotal: selectTotalSharedNum,
} = sharedLibraryAdapter.getSelectors(
  (state: RootState) => selectSharedData(state) ?? initialState
);
